import React, { useState, useEffect, useContext } from "react";
import { ConfigContext } from "../../Context/ConfigContext";
import axios from "axios";
import { NavLink, useSearchParams } from "react-router-dom";
import { OverlayLoader } from "../../Components/Shimmers";
import AccountSiderBar from "./AccountSideBar";
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import dayjs from "dayjs";
import "../../styles/Pagination.css";
import Config from "../../appConfig";
import { GetOrderStatus } from "../../Components/InputsComponents";
import { axiosGet } from "../../Apis/axiosMethods";

const MyOrders = () => {

    const { t } = useTranslation();
    const { currency } = useContext(ConfigContext);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const [searchParams, setSearchParams] = useSearchParams();

    const initialPage = Number(searchParams.get("page")) || 1;
    const initialLimit = Number(searchParams.get("limit")) || 20;

    const [limit, setLimit] = useState(initialLimit);
    const [page, setPage] = useState(initialPage);


    const [total_pages, settotal_pages] = useState(0);
    const [total_records, setTotal_records] = useState(0);

    const [next, setNext] = useState(false);
    const [prev, setPrev] = useState(false);


    useEffect(() => {
        GetOrders();
    }, [page, limit]);

    const GetOrders = async () => {
        try {
            setLoading(true);
            const response = await axiosGet(`/Orders/GetOrders`, {
                page,
                limit,
                currency
            });

            const data = response?.data;

            if (data.success) {
                setOrders(data.data);
                setTotal_records(data?.total_records);
                setPrev(data?.prev);
                setNext(data?.next);
                setPage(data?.page);
                settotal_pages(data?.total_pages);
            } else {
                console.log(
                    "Error While Fetching Customer Orders",
                    data.message
                );
            }
        } catch (error) {
            console.log("Oops Something Went Wrong", error);
        } finally {
            setLoading(false);
        }
    };

    const downloadExcel = async () => {

        try {

            const response = await axiosGet(`/Orders/GetOrders?limit=${total_records}`,);

            if (!response.data.success) {
                alert("Failed to download orders");
                return;
            }

            const allOrders = response.data.data;

            const worksheet = XLSX.utils.json_to_sheet(
                allOrders.map(order => ({
                    "Order ID": order.secret_order_id,
                    "Date": dayjs(order.business_order_date).format("DD-MM-YYYY"),
                    "Status": order.business_order_status_label,
                    "Total": order.business_order_grand_total,
                    "Due Amount": order.business_balance_amount
                }))
            );

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

            const excelBuffer = XLSX.write(workbook, {
                bookType: "xlsx",
                type: "array"
            });

            const fileData = new Blob(
                [excelBuffer],
                { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
            );

            saveAs(fileData, "orders.xlsx");

        } catch (error) {

            console.log("Excel Download Error =>", error);
            alert("Excel Download Failed");

        }
    };

    const downloadPDF = async () => {

        try {

            const response = await axiosGet(`/Orders/GetOrders?limit=${total_records}`,);

            if (!response.data.success) {
                alert("PDF download failed");
                return;
            }

            const allOrders = response.data.data;

            const doc = new jsPDF();

            const tableColumn = [
                "Order ID",
                "Date",
                "Status",
                "Total",
                "Due Amount"
            ];

            const tableRows = allOrders.map(order => [
                order.secret_order_id,
                dayjs(order.business_order_date).format("DD-MM-YYYY"),
                order.business_order_status_label,
                order.business_order_grand_total,
                order.business_balance_amount
            ]);

            doc.autoTable({
                head: [tableColumn],
                body: tableRows,
                startY: 20,
                theme: "grid",
                styles: { fontSize: 8 }
            });

            doc.save("orders.pdf");

        } catch (error) {

            console.log("PDF Download Error =>", error);
            alert("PDF Download Failed");

        }
    };

    const dir = localStorage.getItem('dir');

    const getPageNumbers = () => {
        const pages = [];
        const total = total_pages;
        const windowSize = 5;

        let start = page;
        let end = page + windowSize - 1;

        // If end exceeds total pages
        if (end > total) {
            end = total;
            start = total - windowSize + 1;
        }

        // If start goes below 1
        if (start < 1) {
            start = 1;
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        return pages;
    };

    useEffect(() => {
        setSearchParams({
            page: page,
            limit: limit
        });
    }, [page, limit]);


    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > total_pages) return;

        setPage(newPage);

        setSearchParams({
            page: newPage,
            limit: limit
        });

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    const handleLimitChange = (e) => {
        const newLimit = Number(e.target.value);

        setLimit(newLimit);
        setPage(1);

        setSearchParams({
            page: 1,
            limit: newLimit
        });

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    useEffect(() => {
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            params.set("page", page);
            params.set("limit", limit);
            return params;
        }, { replace: true });
    }, [page, limit]);


    return (
        <>
            {
                loading ?
                    <div style={{ height: "70vh" }}>
                        <OverlayLoader />
                    </div>
                    :
                    <main style={{ marginTop: '-10px' }}>
                        <div className="page-header">
                            <div className="container d-flex flex-column align-items-center">
                                <nav aria-label="breadcrumb" className="breadcrumb-nav">
                                    <div className="container">
                                        <ol className="breadcrumb" dir={dir}>
                                            <li className="breadcrumb-item">
                                                <NavLink to={'/'}>{t("home")}</NavLink>
                                            </li>
                                            <li className="breadcrumb-item">
                                                <NavLink to={`/MyAccount`}>{t("my_account")}</NavLink>
                                            </li>
                                            <li className="breadcrumb-item active" aria-current="page">
                                                {t("orders")}
                                            </li>
                                        </ol>
                                    </div>
                                </nav>
                                <h1>{t("orders")}</h1>
                            </div>
                        </div>
                        <div className="container account-container custom-account-container">
                            <div className="row">
                                <AccountSiderBar />
                                <div className="col-lg-12 order-lg-last order-1 tab-content">
                                    <div className="tab-pane fade show active" id="address" role="tabpanel">
                                        <div className="order-content">
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '1rem'
                                            }}>
                                                <h3 className={`account-sub-title d-none d-md-block mb-0 ${dir === 'rtl' ? 'text-right' : ''}`} dir={dir}>
                                                    <i className="sicon-social-dropbox align-middle mr-3" />
                                                    {t("orders")}
                                                </h3>
                                                {orders.length > 0 && (
                                                    <div>
                                                        <label className="mr-2">{t("Download orders report")}: </label>
                                                        <button
                                                            onClick={downloadExcel}
                                                            className="btn btn-primary"

                                                            title={t("download_excel")}
                                                        >
                                                            <i className="fas fa-file-excel" style={{ color: '#FFF', fontSize: '1.2rem' }}></i> EXCEL
                                                        </button>
                                                        <button
                                                            onClick={downloadPDF}
                                                            className="btn btn-primary ml-2"

                                                            title={t("download_pdf")}
                                                        >
                                                            <i className="fas fa-file-pdf" style={{ color: '#FFF', fontSize: '1.2rem' }}></i> PDF
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="order-table-container text-center">
                                                <table className="table table-order text-left">
                                                    <thead>
                                                        <tr>
                                                            <th className="order-id">Sr No.</th>
                                                            <th className="order-id">{t("order")}</th>
                                                            <th className="order-date">{t("date")}</th>
                                                            <th className="order-status">{t("status")}</th>
                                                            <th className="order-price">{t("total")}</th>
                                                            <th className="order-action">{t("actions")}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {orders.length > 0 ? (
                                                            orders.map((order, index) => (
                                                                <tr key={order.secret_order_id}>

                                                                    <td className="order-id">
                                                                        {((page - 1) * limit) + index + 1}
                                                                    </td>


                                                                    <td className="order-id" style={{ fontWeight: "bold" }}>
                                                                        <NavLink to={`/MyAccount/Orders/${order.secret_order_id}`} className={`nav-link`}>
                                                                            {order.secret_order_id}
                                                                        </NavLink>
                                                                    </td>
                                                                    <td className="order-date text-dark" style={{ fontWeight: "bold" }}>{dayjs(order.business_order_date).format("DD-MM-YYYY")}</td>

                                                                    <td className="order-status">
                                                                        {(() => {
                                                                            const status = GetOrderStatus(order.business_order_status);

                                                                            return (
                                                                                <span className={`${status.class} text-white`}>
                                                                                    {status.label}
                                                                                </span>
                                                                            );
                                                                        })()}
                                                                    </td>

                                                                    {
                                                                        order?.business_order_status === 7 ?
                                                                            <td className="text-success" style={{ fontWeight: "bold" }}>
                                                                                <span>Order Fully Returned</span>
                                                                            </td>
                                                                            : order?.business_order_status === 6 ?
                                                                                <td className="text-danger" style={{ fontWeight: "bold" }}>
                                                                                    <span>Order Cancelled</span>
                                                                                </td> :
                                                                                <td className="text-dark" style={{ fontWeight: "bold" }}>
                                                                                    {order?.display_business_order_grand_total}
                                                                                </td>
                                                                    }

                                                                    <td className="order-action">
                                                                        <NavLink to={`/MyAccount/Orders/${order.secret_order_id}`} className={`nav-link`}>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-primary btn-sm"
                                                                                style={{ padding: '0.2em 0.5em', fontSize: '1.1rem' }}>
                                                                                <i className="fa fa-info"></i> {t("details")}
                                                                            </button>
                                                                        </NavLink>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td className="text-center p-0" colSpan={5}>
                                                                    <p className="mb-5 mt-5">{t("no_order_yet_made")}</p>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr>
                                                            <td colSpan={9}>

                                                                <div className="d-flex justify-content-end align-items-center flex-wrap gap-2">

                                                                    {/* Show entries info */}
                                                                    <div className="show-entries">
                                                                        Showing{" "}
                                                                        {total_records === 0 ? 0 : (page - 1) * limit + 1}{" "}
                                                                        to{" "}
                                                                        {Math.min(page * limit, total_records)}{" "}
                                                                        of {total_records} entries
                                                                    </div>


                                                                    {/* Right side controls */}
                                                                    <div className="d-flex align-items-center gap-3">

                                                                        {/* Pagination */}
                                                                        <nav>
                                                                            <ul className="custom-pagination mb-0">
                                                                                <li
                                                                                    className={`page-btn ${page === 1 ? "disabled" : ""}`}
                                                                                    onClick={() => page > 1 && handlePageChange(1)}
                                                                                >
                                                                                    &laquo;
                                                                                </li>

                                                                                {/* Previous */}
                                                                                <li
                                                                                    className={`page-btn ${page === 1 ? "disabled" : ""}`}
                                                                                    onClick={() => page > 1 && handlePageChange(page - 1)}
                                                                                >
                                                                                    &lt;
                                                                                </li>

                                                                                {/* Pages */}
                                                                                {getPageNumbers().map((p, index) => (
                                                                                    p === "..." ? (
                                                                                        <li key={`dots-${index}`} className="page-btn dots">...</li>
                                                                                    ) : (
                                                                                        <li
                                                                                            key={p}
                                                                                            className={`page-btn ${p === page ? "active" : ""}`}
                                                                                            onClick={() => handlePageChange(p)}
                                                                                        >
                                                                                            {p}
                                                                                        </li>
                                                                                    )
                                                                                ))}

                                                                                {/* Next */}
                                                                                <li
                                                                                    className={`page-btn ${page === total_pages ? "disabled" : ""}`}
                                                                                    onClick={() => page < total_pages && handlePageChange(page + 1)}
                                                                                >
                                                                                    &gt;
                                                                                </li>

                                                                                <li
                                                                                    className={`page-btn ${page === total_pages ? "disabled" : ""}`}
                                                                                    onClick={() => page < total_pages && handlePageChange(total_pages)}
                                                                                >
                                                                                    &raquo;
                                                                                </li>

                                                                            </ul>
                                                                        </nav>

                                                                        <div className="d-flex align-items-center" style={{ marginLeft: "10px" }}>
                                                                            <select
                                                                                className="form-control form-control-sm"
                                                                                value={limit}
                                                                                onChange={handleLimitChange}
                                                                            >
                                                                                <option value={20}>20</option>
                                                                                <option value={50}>50</option>
                                                                                <option value={100}>100</option>
                                                                                <option value={150}>150</option>
                                                                                <option value={200}>200</option>
                                                                                <option value={250}>250</option>
                                                                            </select>
                                                                        </div>

                                                                    </div>
                                                                </div>


                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
            }
        </>
    );
};

export default MyOrders;
